import WellnessStateCircle from '@/components/molecules/WellnessStateCircle';
import CustomButton from '@/components/atoms/CustomButton';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import svgIcons from '@/assets/svg/icons';
import colors from '@/theme/colors';
import { EMPTY_WELLNESS_DOMAINS, WELLNESS_DATA_MULTIPLIER, WELLNESS_DOMAINS_DISPLAY_LABELS } from '@/constants';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { TDashboardScores } from '@/types/Dashboard.types';
import { TWholenessObservation } from '@/types/Organization.types';
import { DASHBOARD_RIGHT_PANEL_WIDTH_PX, OVERVIEW_RIGHT_SIDE_PANEL_HEIGHT } from '@/constants/dimensions';
import OverviewSectionContainer from './OverviewSectionContainer';
import { THealthDomain, THealthDomainWithOverall } from '@/types/Health.types';
import YourScore from './YourScore';
import OrganizationOverview from './OrganizationOverview';
import { useAppStore } from '@/store/useAppStore';
import useCortexOrganizationInsights from '@/hooks/cortex/useCortexOrganizationInsights';
import useSendScreenerReminder from '@/hooks/notifications/useSendScreenerReminder';
import { NO_ID } from '@/constants/stringVars';

interface Props {
  dashboardScores?: TDashboardScores;
  avatarTooltipJSX?: React.JSX.Element | null;
  showCenteredTextOverlay?: boolean;
  title?: string;
  subtitle?: string;
  isOrganizationOverview?: boolean;
  interpretationText?: string;
  additionalOverviewSection?: React.JSX.Element;
  overviewBottomComponent?: React.JSX.Element;
  selectedDomain?: THealthDomainWithOverall | undefined;
  setSelectedDomain?: Dispatch<SetStateAction<THealthDomainWithOverall | undefined>>;
  titleInTopRow?: boolean;
  isHoverable?: boolean;
  isSendScreenerReminderButtonShown?: boolean;
  wholenessObservation?: TWholenessObservation;
  organizationName?: string;
}

const WholenessRingWithOverview = ({
  dashboardScores,
  avatarTooltipJSX,
  showCenteredTextOverlay = true,
  title = 'Wholeness Ring & Overview',
  subtitle = 'This graph displays the total scores of your wholeness broken down per domain.',
  isOrganizationOverview = false,
  interpretationText,
  additionalOverviewSection,
  overviewBottomComponent,
  selectedDomain,
  setSelectedDomain,
  titleInTopRow = false,
  isHoverable,
  isSendScreenerReminderButtonShown = false,
  wholenessObservation,
  organizationName,
}: Props) => {
  const totalScore = (dashboardScores?.totalScore ?? 1) * WELLNESS_DATA_MULTIPLIER;

  const { user } = useAppStore();

  const { data: cortexInsightsResult, isInsightsGenerating } = useCortexOrganizationInsights(
    user?.app_metadata.primaryOrganizationId ?? NO_ID,
  );

  const [hoveredDomain, setHoveredDomain] = useState<THealthDomain | undefined>(undefined);

  const [isSendReminderConfirmationModalOpen, setIsSendReminderConfirmationModalOpen] = useState(false);

  const {
    mutate: sendScreenerReminder,
    error: sendScreenerReminderError,
    isPending: isSendingScreenerReminder,
  } = useSendScreenerReminder(() => {
    setIsSendReminderConfirmationModalOpen(false);
  });

  return (
    <VStack width={'100%'}>
      {titleInTopRow && (
        <VStack width={'100%'} justifyContent={'center'} gap={'10px'}>
          <Text variant="loraSectionTitle" textAlign={'start'} color="text.darkBlue">
            {title}
          </Text>
          <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'} marginBottom={'25px'}>
            {subtitle}
          </Text>
        </VStack>
      )}
      <HStack width={'100%'} gap={'60px'} align={'start'} justifyContent={'space-between'}>
        {/* Wholeness Ring */}
        <VStack width={'60%'} justifyContent={'center'} gap={'10px'}>
          {!titleInTopRow && (
            <VStack width={'100%'} justifyContent={'center'} gap={'10px'}>
              <Text variant="loraSectionTitle" textAlign={'start'} color="text.darkBlue">
                {title}
              </Text>
              <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'} marginBottom={'25px'}>
                {subtitle}
              </Text>
            </VStack>
          )}
          <WellnessStateCircle
            wellnessData={dashboardScores?.domains ?? EMPTY_WELLNESS_DOMAINS}
            totalScore={dashboardScores?.totalScore}
            selectedDomain={selectedDomain as THealthDomain}
            setSelectedDomain={setSelectedDomain}
            showCenteredTextOverlay={showCenteredTextOverlay}
            avatarTooltipJSX={avatarTooltipJSX}
            greenScoreDescription={isOrganizationOverview ? 'Team Strengths' : 'Your Strengths'}
            hoveredDomain={hoveredDomain}
            setHoveredDomain={isHoverable ? setHoveredDomain : undefined}
            hoveredDomainContent={
              hoveredDomain &&
              (() => {
                const strength = wholenessObservation?.strengthDomains.find((d) => d.domainKey === hoveredDomain);
                const opportunity = wholenessObservation?.opportunityDomains.find((d) => d.domainKey === hoveredDomain);
                const matched = strength ?? opportunity;

                const score = matched?.score ?? Math.round(dashboardScores?.domains?.[hoveredDomain] ?? 0);
                if (!score) return undefined;

                const domainName =
                  matched?.domainName ?? `${WELLNESS_DOMAINS_DISPLAY_LABELS[hoveredDomain] ?? hoveredDomain} Wellness`;
                const label = strength
                  ? 'Top strength domain'
                  : opportunity
                    ? 'Top opportunity for growth'
                    : 'Team average over the last 30 days';

                return (
                  <VStack
                    backgroundColor={'white'}
                    padding={'20px'}
                    borderRadius={'8px'}
                    boxShadow="0px 6.97px 34.86px 0px #00417930"
                    gap={'4px'}
                  >
                    <Text variant={'urbanistBold'} color={'text.mediumBlue'}>
                      {domainName} — {score}/100
                    </Text>
                    <Text variant={'urbanistMedium'} color={'text.mediumBlue'}>
                      {label}
                    </Text>
                  </VStack>
                );
              })()
            }
          />
          {isSendScreenerReminderButtonShown && (
            <HStack width={'100%'} justifyContent={'center'} marginTop={'30px'}>
              <CustomButton
                label={'Send Screener Reminder'}
                noShadow
                hoverOpacityDrop={true}
                onClick={() => setIsSendReminderConfirmationModalOpen(true)}
                iconBeforeLabelSvgPath={svgIcons.curvedArrowRight}
                iconBeforeLabelSvgSize={16}
                labelColor={colors.text.darkBlue}
                borderColor={colors.text.mediumBlue}
                labelHoverColor={colors.text.mediumBlue}
                hoverBackgroundColor={'white'}
                width={'320px'}
              />
            </HStack>
          )}
        </VStack>
        {/* Overview Right Side Panel */}
        {isOrganizationOverview ? (
          <OrganizationOverview
            totalScore={totalScore}
            additionalOverviewSection={additionalOverviewSection}
            overviewBottomComponent={overviewBottomComponent}
            cortexInsightsResult={cortexInsightsResult}
            isInsightsGenerating={isInsightsGenerating}
            wholenessObservation={wholenessObservation}
            organizationName={organizationName}
          />
        ) : (
          <VStack
            width={DASHBOARD_RIGHT_PANEL_WIDTH_PX}
            height={`${OVERVIEW_RIGHT_SIDE_PANEL_HEIGHT}px`}
            borderRadius={'20px'}
            gap={'16px'}
          >
            {/* Your Score */}
            <OverviewSectionContainer>
              <YourScore score={totalScore} svgId="score-overview" />
            </OverviewSectionContainer>

            <OverviewSectionContainer>
              <Text variant={'urbanistMediumLarge'} color={'text.mediumBlue'}>
                {interpretationText}
              </Text>
            </OverviewSectionContainer>

            {additionalOverviewSection && (
              <OverviewSectionContainer>{additionalOverviewSection}</OverviewSectionContainer>
            )}

            {overviewBottomComponent ? overviewBottomComponent : <VStack height={'50px'} />}
          </VStack>
        )}
      </HStack>

      {isSendScreenerReminderButtonShown && (
        <>
          <ConfirmationModal
            title="Send Screener Reminder"
            description="This will send a reminder to every organization member to retake the screener."
            confirmButtonText="Send"
            isOpen={isSendReminderConfirmationModalOpen}
            onClose={() => setIsSendReminderConfirmationModalOpen(false)}
            confirmLoading={isSendingScreenerReminder}
            onConfirm={sendScreenerReminder}
            errorText={sendScreenerReminderError?.errors[0].message}
          />
        </>
      )}
    </VStack>
  );
};

export default WholenessRingWithOverview;
